import { ReactNode, useCallback, useState } from 'react'
import { NotificationContext } from '../context'

/**
 * NotificationProvider component provides a context for managing notifications.
 *
 * @param {Object} props - The props for the component.
 * @param {ReactNode} props.children - The child components that will have access to the notification context.
 *
 * @returns {JSX.Element} The NotificationProvider component.
 */
export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const [notifications, setNotifications] = useState<
        Record<string, NotificationType>
    >({})

    /**
     * Show a notification.
     *
     * @param {string} name - The unique name/identifier for the notification.
     * @param {NotificationType} newNotification - The notification data to be displayed.
     */
    const showNotification = (
        name: string,
        newNotification: NotificationType
    ) => {
        setNotifications((prevState) => ({
            ...prevState,
            [name]: newNotification,
        }))
    }

    /**
     * Hide a notification.
     *
     * @param {string} name - The unique name/identifier for the notification to be hidden.
     */
    const hideNotification = (name: string) => {
        setNotifications((prevState) => {
            const next = { ...prevState }
            delete next[name]
            return next
        })
    }

    /**
     * Reset all notifications after a delay of 2500 milliseconds.
     */
    const resetNotifications = useCallback(() => {
        setTimeout(() => {
            setNotifications({})
        }, 2500)
    }, [])

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                showNotification,
                hideNotification,
                resetNotifications,
            }}
        >
            {children}
        </NotificationContext.Provider>
    )
}
